import { agentClient } from "../../lib/agentClient";
import { bearer } from "../../lib/accessToken";
import type { JournalSummary } from "../../../../shared/work-journal";

export interface JournalScope {
  token: string;
  orgId: string;
  projectId: string;
  userId: string;
  threadId: string;
  /** The workflow the conversation belongs to: its journal lives under its routes. */
  workflowId: string;
}

function query(scope: JournalScope): string {
  return new URLSearchParams({
    organization_id: scope.orgId,
    project_id: scope.projectId,
    user_id: scope.userId,
  }).toString();
}

/** `GET /workflows/:id/threads/:threadId/<section>` */
function threadPath(scope: JournalScope, section: string): string {
  return `/workflows/${encodeURIComponent(scope.workflowId)}/threads/${encodeURIComponent(scope.threadId)}/${section}`;
}

async function read<T>(path: string, scope: JournalScope, signal: AbortSignal): Promise<T> {
  const response = await agentClient.fetch(path, { signal, headers: { Authorization: bearer(scope.token) } });
  const body = await response.json();
  if (!response.ok)
    throw new Error(typeof body.error === "string" ? body.error : (body.error?.message ?? body.message ?? `HTTP ${response.status}`));
  return body as T;
}

/** The thread's journal without tool input or output, oldest first; the agent reads those itself. */
export async function readJournal(scope: JournalScope, signal: AbortSignal): Promise<JournalSummary[]> {
  const body = await read<{ entries?: JournalSummary[] }>(`${threadPath(scope, "journal")}?${query(scope)}`, scope, signal);
  return Array.isArray(body.entries) ? body.entries : [];
}

/** A step image the journal saved, as a Blob; the request carries the token, so an <img> cannot fetch it itself. */
export async function readJournalImage(scope: JournalScope, path: string, signal: AbortSignal): Promise<Blob> {
  const response = await agentClient.fetch(`${threadPath(scope, "journal/image")}?${query(scope)}&${new URLSearchParams({ path })}`, {
    signal, headers: { Authorization: bearer(scope.token) },
  });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return response.blob();
}

/**
 * A tool call and its result as one row. `unconfirmed` is a call with no
 * result after the run ended: the host may still have done the work.
 */
export interface JournalStep {
  kind: "step";
  /** The entry's number, as the user and the agent refer to it. */
  n: number;
  resultN: number | null;
  at: string;
  tool: string;
  displayName?: string;
  state: "done" | "failed" | "running" | "unconfirmed";
  /** Images the tool returned, saved in the Workspace: what the agent looked at. */
  images?: string[];
}

export interface JournalSaid {
  kind: "said";
  n: number;
  at: string;
  text: string;
}

/** The run was picked up from its checkpoint here, with no new message from the user. */
export interface JournalContinued {
  kind: "continued";
  n: number;
  at: string;
}

export type JournalItem = JournalStep | JournalSaid | JournalContinued;

/** What followed one user message; the journal's start may have none. */
export interface JournalGroup {
  key: string;
  user: { n: number; at: string; text: string } | null;
  items: JournalItem[];
}

/** The journal as the panel shows it; `live` while a run may still add to the last group. */
export function toTimeline(entries: readonly JournalSummary[], live: boolean): JournalGroup[] {
  const groups: JournalGroup[] = [];
  const open = new Map<string, JournalStep>();
  const current = (): JournalGroup => {
    if (!groups.length) groups.push({ key: "start", user: null, items: [] });
    return groups[groups.length - 1]!;
  };
  for (const entry of entries) {
    if (entry.type === "user") {
      groups.push({ key: `u${entry.n}`, user: { n: entry.n, at: entry.at, text: entry.text }, items: [] });
    } else if (entry.type === "assistant") {
      current().items.push({ kind: "said", n: entry.n, at: entry.at, text: entry.text });
    } else if (entry.type === "continue") {
      // Picked up from the checkpoint: the same user message's work goes on, so no new group.
      current().items.push({ kind: "continued", n: entry.n, at: entry.at });
    } else if (entry.type === "tool_call") {
      const step: JournalStep = {
        kind: "step", n: entry.n, resultN: null, at: entry.at, tool: entry.tool,
        ...(entry.displayName ? { displayName: entry.displayName } : {}), state: "unconfirmed",
      };
      current().items.push(step);
      open.set(entry.id, step);
    } else if (entry.type === "tool_result") {
      const step = open.get(entry.id);
      if (!step) continue;
      step.resultN = entry.n;
      step.state = entry.ok ? "done" : "failed";
      if (entry.images?.length) step.images = entry.images;
      open.delete(entry.id);
    }
  }
  if (live) {
    const last = groups.at(-1);
    for (const step of open.values()) if (last?.items.includes(step)) step.state = "running";
  }
  return groups;
}

/** A run of steps between two of the agent's messages, or a slice of a long one. */
export interface JournalStepBlock {
  kind: "steps";
  key: string;
  steps: JournalStep[];
}

export type JournalBlock = { kind: "said"; said: JournalSaid } | { kind: "continued"; mark: JournalContinued } | JournalStepBlock;

/**
 * Where a cut-off run stopped, for the mark the panel draws: the last step whose
 * result never came back (the run died inside it), or null when the run stopped
 * between steps — the model was working out the next one.
 */
export function stoppedAtStep(groups: readonly JournalGroup[]): JournalStep | null {
  const last = groups.at(-1);
  if (!last) return null;
  const steps = last.items.filter((item): item is JournalStep => item.kind === "step");
  return [...steps].reverse().find((step) => step.state === "unconfirmed") ?? null;
}

/** A run longer than this, with no message from the agent, is sliced into blocks this long. */
export const JOURNAL_BLOCK_STEPS = 10;
/** A block this short is shown as is: folding one or two steps hides more than it saves. */
export const JOURNAL_LOOSE_STEPS = 3;

/**
 * A group's items as the panel folds them. The agent's own messages divide the
 * steps, which reads naturally; only a run with none is sliced every ten steps.
 * A slice is labelled by its count, never by position, and a tail too short to
 * fold joins the slice before it: no fold is ever followed by stray steps.
 */
export function toBlocks(items: readonly JournalItem[]): JournalBlock[] {
  const blocks: JournalBlock[] = [];
  let run: JournalStep[] = [];
  const flush = () => {
    if (run.length > JOURNAL_BLOCK_STEPS) {
      for (let i = 0; i < run.length; i += JOURNAL_BLOCK_STEPS) {
        const rest = run.length - (i + JOURNAL_BLOCK_STEPS);
        const steps = run.slice(i, rest <= JOURNAL_LOOSE_STEPS ? run.length : i + JOURNAL_BLOCK_STEPS);
        blocks.push({ kind: "steps", key: `s${steps[0]!.n}`, steps });
        if (rest <= JOURNAL_LOOSE_STEPS) break;
      }
    } else if (run.length) {
      blocks.push({ kind: "steps", key: `s${run[0]!.n}`, steps: run });
    }
    run = [];
  };
  for (const item of items) {
    if (item.kind === "said") {
      flush();
      blocks.push({ kind: "said", said: item });
    } else if (item.kind === "continued") {
      // A continue divides the steps like one of the agent's messages: before it, the cut; after it, the pick-up.
      flush();
      blocks.push({ kind: "continued", mark: item });
    } else {
      run.push(item);
    }
  }
  flush();
  return blocks;
}

/** What a folded row says about its steps: how many, and the ones that did not go well. */
export function stepCounts(steps: readonly JournalStep[]): { total: number; failed: number; unconfirmed: number; running: number } {
  return {
    total: steps.length,
    failed: steps.filter((step) => step.state === "failed").length,
    unconfirmed: steps.filter((step) => step.state === "unconfirmed").length,
    running: steps.filter((step) => step.state === "running").length,
  };
}

/**
 * The question a clicked step puts in the composer. It names the step the way
 * the user saw it and its number in the journal, which the agent reads to answer.
 */
export function askAbout(step: JournalStep, name: string, t: (key: string, vars?: Record<string, string | number>) => string): string {
  return t("workJournal.question", { ref: t("workJournal.ref", { n: step.n }), name });
}
